import { useEffect, useRef } from "react";
import { useBackButton } from "@/store/use-back-button";

/**
 * Makes the browser/Android back button close an overlay instead of leaving
 * the page.
 *
 * While the overlay is open it owns a history entry of its own — same URL,
 * same router state, so the router sees it as the page it is already on — and
 * a back press pops that entry rather than the page underneath. Closing the
 * overlay any other way (a button, a swipe, Escape) gives the entry back, so
 * the extra entry never survives the thing that pushed it and back never needs
 * pressing twice.
 *
 * Entries stack: a drawer and a screen drilled into it each take one, and back
 * unwinds them innermost-first.
 *
 * Inside the Eitaa mini app there is no browser chrome and no gesture to go
 * back with — the host's own back button is the only one there is, and it only
 * appears while something has claimed it. So the overlay claims it too, which
 * is what makes back work on the pages that show no back button of their own
 * (the tabs reached from the bottom bar). The two routes are independent and
 * both simply close the overlay, so whichever the platform offers is the one
 * that fires.
 */

/** The key our marker lives under in `history.state`. */
const OVERLAY_KEY = "__overlays";

interface Overlay {
  id: string;
  dismiss: () => void;
}

/** The overlays that are open, oldest first — one history entry each. */
const stack: Overlay[] = [];

/** Steps handed to `history.go`, waiting on the `popstate` that lands them. */
let requested = 0;
let flushScheduled = false;
let listening = false;
let seq = 0;

type HistoryState = Record<string, unknown> | null;

/** The overlay ids carried by the entry we are sitting on. */
function currentIds(): string[] {
  const marker = (window.history.state as HistoryState)?.[OVERLAY_KEY];
  return Array.isArray(marker) ? (marker as string[]) : [];
}

function listen() {
  if (listening) return;
  listening = true;
  window.addEventListener("popstate", handlePopState);
}

function handlePopState() {
  // Whatever traversal we had asked for has landed — or the user beat us to it.
  // Either way the entry we are on is the truth from here.
  requested = 0;

  const ids = new Set(currentIds());
  // The pop stepped over some of our entries: close their overlays, innermost
  // first, and stop at the first one that survived.
  for (let i = stack.length - 1; i >= 0; i -= 1) {
    if (ids.has(stack[i].id)) break;
    const [overlay] = stack.splice(i, 1);
    overlay.dismiss();
  }

  scheduleFlush();
}

function push(overlay: Overlay) {
  listen();
  stack.push(overlay);
  // Keep the state the router put there — `key` and `idx` included — so the
  // entry reads as the very page we are on and going back is a no-op for it.
  window.history.pushState(
    {
      ...(window.history.state as HistoryState),
      [OVERLAY_KEY]: [...currentIds(), overlay.id],
    },
    "",
  );
}

function pop(overlay: Overlay) {
  const index = stack.indexOf(overlay);
  // Already gone — a back press took it, which is the whole point of the hook.
  if (index === -1) return;
  stack.splice(index, 1);
  scheduleFlush();
}

/**
 * Deferred to a microtask so a tick that closes two overlays — or closes one
 * and opens another in its place — settles first and gives the browser a
 * single traversal rather than several racing ones.
 */
function scheduleFlush() {
  if (flushScheduled) return;
  flushScheduled = true;
  queueMicrotask(flush);
}

/** Hand back the trailing history entries whose overlays have closed. */
function flush() {
  flushScheduled = false;
  // A traversal is still in flight; its `popstate` schedules the next look.
  if (requested > 0) return;

  const ids = currentIds();
  const live = new Set(stack.map((overlay) => overlay.id));
  let steps = 0;
  // Only the tail can be given back: an entry buried under a live one stays,
  // costing nothing — it is the same URL as the entry above it.
  while (steps < ids.length && !live.has(ids[ids.length - 1 - steps])) {
    steps += 1;
  }
  if (steps === 0) return;

  requested = steps;
  window.history.go(-steps);
}

/**
 * @param active Whether the overlay is open and should answer the back button.
 * @param onDismiss Closes it. Read fresh on every back press, so an inline
 *   arrow is fine.
 */
export function useBackDismiss(active: boolean, onDismiss: () => void) {
  const dismissRef = useRef(onDismiss);
  useEffect(() => {
    dismissRef.current = onDismiss;
  });

  useEffect(() => {
    if (!active) return;
    const overlay: Overlay = {
      id: `overlay-${(seq += 1)}`,
      dismiss: () => dismissRef.current(),
    };
    push(overlay);
    return () => pop(overlay);
  }, [active]);

  // Claimed for as long as the overlay is open, and always on top of whatever
  // the page beneath registered, so the host's button closes the overlay before
  // it ever goes back a page.
  useBackButton(onDismiss, active);
}
